// src/api/boards.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as firestoreLimit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import Fuse from 'fuse.js';
import {
  isRagAnswerEnabledFromPage,
  isWebPageIntendedFromPage,
  type PagePublishingData,
} from '../utils/pagePublishing';

const buildPublicPageMap = (
  snapshot: { forEach: (cb: (doc: { id: string; data: () => any }) => void) => void }
): Map<string, PagePublishingData> => {
  const map = new Map<string, PagePublishingData>();
  snapshot.forEach((docSnap) => {
    const data = docSnap.data() as any;
    map.set(docSnap.id, {
      status: data?.status,
      published: data?.published,
      webPageEnabled: data?.webPageEnabled,
    });
  });
  return map;
};

/**
 * ノート一覧用に公開状態を 2 軸へ分解する。
 * `publicPageStatus` は並び替えの互換のために残している。
 */
const derivePublicAxes = (publicMeta: PagePublishingData | undefined) => {
  const publicRagEnabled = isRagAnswerEnabledFromPage(publicMeta);
  const publicWebIntended = publicRagEnabled && isWebPageIntendedFromPage(publicMeta);
  const publicStatus: 'nonPublic' | 'standby' | 'published' = !publicRagEnabled
    ? 'nonPublic'
    : publicMeta?.published === true
      ? 'published'
      : 'standby';
  return { publicStatus, publicRagEnabled, publicWebIntended };
};

export type BoardData = {
  id: string;
  title?: string;
  content?: string;
  firstImageUrl?: string | null;
  lastModified?: Timestamp | number | Date;
  lastModifiedBy?: string;
  version?: number;
  slug?: string;
  archived?: boolean;
  pinned?: boolean;
  previewData?: any;
  relatedThreadIds?: string[];
  // thread summary linkage
  sourceThreadId?: string;
  summaryFormat?: 'summary' | 'faq';
  generatedAt?: any;
  generatedFromThread?: boolean;
  searchData?: {
    hashtags?: string[];
    originalContent?: string;
    normalizedContent?: string;
    originalTitle?: string;
    normalizedTitle?: string;
  };
  hashtags?: string[]; // 互換性のために残す（searchData.hashtagsを優先）
  editData?: any; // 編集データ（ydocBytes等を含む）
  isCrawled?: boolean; // クローラーで取得されたページかどうか
  [key: string]: any;
};

/**
 * Fuse.js の検索オプション設定
 * タイトル・コンテンツ・タグを重み付けして曖昧検索
 */
const getFuseSearchOptions = () => {
  return {
    keys: [
      { name: 'title', weight: 1.0 },                          // タイトルに最高重み
      { name: 'searchData.originalTitle', weight: 0.9 },       // オリジナルタイトル
      { name: 'searchData.normalizedTitle', weight: 0.8 },     // 正規化タイトル
      { name: 'searchData.hashtags', weight: 0.7 },            // ハッシュタグ
      { name: 'searchData.originalContent', weight: 0.5 },     // コンテンツ（オリジナル）
      { name: 'searchData.normalizedContent', weight: 0.4 },   // コンテンツ（正規化）
    ],
    threshold: 0.3,           // 0.0 = 完全一致のみ、1.0 = 何でもマッチ（0.3 = 適度な曖昧さ）
    distance: 100,            // マッチ位置の許容距離
    minMatchCharLength: 1,    // 最小マッチ文字数
    ignoreLocation: true,     // 文字列内のどこでマッチしてもOK
    includeScore: true,       // スコアを含める（デバッグ用）
    useExtendedSearch: false, // 拡張検索構文は不要
    findAllMatches: true,     // 全てのマッチを検索
  };
};

/**
 * 全ボード検索（簡易版）
 * - Firestore の全文検索は使わず、まずは「全件→クライアント側フィルタ」で破綻しない実装
 * - 件数が多くなったら Algolia / 追加インデックスへ拡張
 */
export const searchAllBoards = async (
  workspaceId: string,
  searchQuery?: string,
  limitCount?: number
): Promise<BoardData[]> => {
  const boardsCol = collection(db, `workspaces/${workspaceId}/boards`);
  
  if (import.meta.env.MODE === 'development') {}
  
  try {
    const publicPagesCol = collection(db, 'publicPages', workspaceId, 'pages');
    const boardConstraints = limitCount
      ? [orderBy('lastModified', 'desc'), firestoreLimit(limitCount)]
      : [orderBy('lastModified', 'desc')];
    const qRef = query(boardsCol, ...boardConstraints);

    const [publicPagesSnap, snap] = await Promise.all([
      getDocs(publicPagesCol),
      getDocs(qRef),
    ]);

    const publicPageMap = buildPublicPageMap(publicPagesSnap);

    if (import.meta.env.MODE === 'development') {
      if (!snap.empty) {
        const firstDoc = snap.docs[0];
        const firstDocData = firstDoc.data();

        // フィールドごとの詳細分析
        Object.entries(firstDocData).forEach(([key, value]) => {});
      }
    }

    const list: BoardData[] = [];
    snap.forEach((d) => {
      const data = d.data() as any;
      const publicMeta = publicPageMap.get(d.id);
      const { publicStatus, publicRagEnabled, publicWebIntended } = derivePublicAxes(publicMeta);
      
      // データ構造に合わせてフィールドを正しくマッピング
      const mappedData: BoardData = {
        // 既存のフィールドをそのまま保持
        ...data,
        // マッピングされたフィールドで上書き
        id: d.id,
        // ✅ 新DB構造: editData.title をフォールバックに含める（一覧でタイトルが空になるのを防ぐ）
        title: data.previewData?.title || data.editData?.title || data.title,
        content: (typeof data.content === 'string' ? data.content : null),
        firstImageUrl: data.previewData?.thumbnailUrl || data.previewData?.firstImageUrl || data.firstImageUrl,
        lastModified: data.lastModified,
        lastModifiedBy: data.lastModifiedBy,
        version: data.version || data.editData?.version,
        slug: data.slug,
        archived: data.archived || false,
        pinned: data.pinned || false,
        previewData: data.previewData,
        searchData: data.searchData,
        // 公開状態メタ
        publicPage: publicMeta,
        publicPageStatus: publicStatus,
        publicPageStatusCategory: publicStatus === 'nonPublic' ? 1 : publicStatus === 'standby' ? 2 : 3,
        publicRagEnabled,
        publicWebIntended,
        // フォルダー情報
        folderId: data.folderId || null,
        ancestorFolderIds: data.ancestorFolderIds || ['root'],
        // クローラー情報
        isCrawled: data.isCrawled || false,
      };

      // (instrumentation removed)
      
      if (import.meta.env.MODE === 'development') {}
      
      list.push(mappedData);
    });

    if (import.meta.env.MODE === 'development') {
      // 各ボードの詳細なフィールド確認
      list.forEach((board, index) => {});
    }

    if (!searchQuery) return list;

    // Fuse.js で曖昧検索を実行
    const fuse = new Fuse(list, getFuseSearchOptions());
    const searchResults = fuse.search(searchQuery);
    
    // Fuse.jsの検索結果から元のBoardDataを抽出
    const filtered = searchResults.map(result => result.item);
    
    if (import.meta.env.MODE === 'development') {

    }
    
    return filtered;
  } catch (error) {
    console.error('❌ [ERROR] searchAllBoards error:', error);
    throw error;
  }
};

/**
 * ハッシュタグでボード検索（簡易版）
 * - hashtags フィールド（string[]）を持つ想定で array-contains を使用
 * - もしサブコレクションで管理している場合は、別途実装に切替
 */
export const searchBoardsByHashtag = async (
  workspaceId: string,
  hashtag: string,
  searchQuery?: string
): Promise<BoardData[]> => {
  try {
    const publicPagesCol = collection(db, 'publicPages', workspaceId, 'pages');
    const publicPagesSnap = await getDocs(publicPagesCol);
    const publicPageMap = buildPublicPageMap(publicPagesSnap);

    const boardsCol = collection(db, `workspaces/${workspaceId}/boards`);

    const qRef = query(
      boardsCol,
      where('searchData.hashtags', 'array-contains', hashtag),
      orderBy('lastModified', 'desc')
    );

    const snap = await getDocs(qRef);

    const list: BoardData[] = [];
    snap.forEach((d) => {
      const data = d.data() as any;

      const publicMeta = publicPageMap.get(d.id);
      const { publicStatus, publicRagEnabled, publicWebIntended } = derivePublicAxes(publicMeta);

      // データ構造に合わせてフィールドを正しくマッピング（実際のDB構造に合わせて修正）
      const boardData: BoardData = {
        // 既存のフィールドをそのまま保持
        ...data,
        // マッピングされたフィールドで上書き
        id: d.id,
        // ✅ 新DB構造: editData.title をフォールバックに含める（一覧でタイトルが空になるのを防ぐ）
        title: data.previewData?.title || data.editData?.title || data.title,
        content: (typeof data.content === 'string' ? data.content : null),
        firstImageUrl: data.previewData?.thumbnailUrl || data.previewData?.firstImageUrl || data.firstImageUrl,
        lastModified: data.lastModified,
        lastModifiedBy: data.lastModifiedBy,
        version: data.version || data.editData?.version,
        slug: data.slug,
        archived: data.archived || false,
        pinned: data.pinned || false,
        previewData: data.previewData,
        searchData: data.searchData,
        // 公開状態メタ
        publicPage: publicMeta,
        publicPageStatus: publicStatus,
        publicPageStatusCategory: publicStatus === 'nonPublic' ? 1 : publicStatus === 'standby' ? 2 : 3,
        publicRagEnabled,
        publicWebIntended,
        // フォルダー情報
        folderId: data.folderId || null,
        ancestorFolderIds: data.ancestorFolderIds || ['root'],
        // クローラー情報
        isCrawled: data.isCrawled || false,
      };

      list.push(boardData);
    });

    if (!searchQuery) {
      return list;
    }

    // Fuse.js で曖昧検索を実行
    const fuse = new Fuse(list, getFuseSearchOptions());
    const searchResults = fuse.search(searchQuery);
    
    // Fuse.jsの検索結果から元のBoardDataを抽出
    const filtered = searchResults.map(result => result.item);
    
    if (import.meta.env.MODE === 'development') {

    }

    return filtered;
  } catch (error) {
    console.error('❌ API: searchBoardsByHashtagエラー', { 
      error, 
      workspaceId, 
      hashtag, 
      searchQuery 
    });
    throw error;
  }
};

/**
 * 単一ボード取得
 */
export const getBoard = async (
  workspaceId: string,
  boardId: string
): Promise<BoardData | null> => {
  try {
    const ref = doc(db, `workspaces/${workspaceId}/boards/${boardId}`);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return null;
    }

    const data = snap.data() as any;
    
    // データ構造に合わせてフィールドを正しくマッピング
    const mappedData: BoardData = {
      ...data,
      id: snap.id,
      title: data.previewData?.title || data.editData?.title || data.title,
      content: data.content,
      firstImageUrl: data.previewData?.thumbnailUrl || data.previewData?.firstImageUrl || data.firstImageUrl,
      lastModified: data.lastModified,
      lastModifiedBy: data.lastModifiedBy,
      version: data.version || data.editData?.version,
      slug: data.slug,
      archived: data.archived || false,
      pinned: data.pinned || false,
      previewData: data.previewData,
      searchData: data.searchData,
      editData: data.editData, // editDataを追加
      // フォルダー情報
      folderId: data.folderId || null,
      ancestorFolderIds: data.ancestorFolderIds || ['root'],
    };

    return mappedData;
  } catch (error) {
    console.error('❌ API: getBoardエラー', { 
      error, 
      workspaceId, 
      boardId 
    });
    throw error;
  }
};

/**
 * ボードのハッシュタグ取得（フィールド直持ち前提）
 * - もし `boards/{id}/hashtags` サブコレクションならここを置き換える
 */
export const getBoardHashtags = async (
  workspaceId: string,
  boardId: string
): Promise<string[]> => {
  try {
    const ref = doc(db, `workspaces/${workspaceId}/boards/${boardId}`);

    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return [];
    }

    const data = snap.data() as BoardData;
    const hashtags = data.searchData?.hashtags || [];

    return hashtags;
  } catch (error) {
    console.error('❌ API: getBoardHashtagsエラー', { 
      error, 
      workspaceId, 
      boardId 
    });
    throw error;
  }
};
